import axios from "axios";
import { useState, useEffect } from "react";

const secret = process.env.REACT_APP_DOORKEEPER_APP_SECRET;
const id = process.env.REACT_APP_DOORKEEPER_APP_ID;
const host = process.env.REACT_APP_DOORKEEPER_APP_URL;

// Module-level state shared by EVERY useOauth() caller. Previously each mounted
// component ran its own full authorization-code exchange, so a single page load
// fanned out into dozens of /oauth/token.json grants. Now all callers share one
// cached token and one in-flight exchange.
let cachedToken = null; // { accessToken, refreshToken, expiresAt }
let inFlight = null; // Promise<string> for the exchange currently in progress

// Treat a token as usable until 30s before it actually expires, to avoid handing
// out a token that dies mid-request.
const isValid = (token) =>
  token && (!token.expiresAt || token.expiresAt > Date.now() + 30000);

const storeToken = (data) => {
  cachedToken = {
    accessToken: data["access_token"],
    refreshToken: data["refresh_token"],
    expiresAt: data["expires_in"]
      ? Date.now() + data["expires_in"] * 1000
      : null,
  };
  return cachedToken.accessToken;
};

// Full authorization-code flow. The OAuth engine's api-only feature is off, so
// the authorize call returns a 302 whose redirect URL carries the code.
const authorizeAndExchange = async () => {
  const authorizeResponse = await axios.get(
    `${host}/oauth/authorize.json?redirect_uri=urn:ietf:wg:oauth:2.0:oob&client_secret=${secret}&client_id=${id}&response_type=code`,
    { withCredentials: true }
  );
  const authCode = authorizeResponse.request.responseURL.split("code=")[1];
  const tokenResponse = await axios({
    method: "POST",
    url: `${host}/oauth/token.json`,
    data: {
      grant_type: "authorization_code",
      client_id: id,
      client_secret: secret,
      redirect_uri: "urn:ietf:wg:oauth:2.0:oob",
      code: authCode,
    },
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
  });
  return tokenResponse.data;
};

// Returns a valid access token, minting one only if the cache is empty/expired
// and coalescing concurrent callers onto a single in-flight request.
const getToken = () => {
  if (isValid(cachedToken)) return Promise.resolve(cachedToken.accessToken);
  if (inFlight) return inFlight;
  inFlight = authorizeAndExchange()
    .then(storeToken)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

// Clears the shared token — call on logout, or between tests.
export function clearCachedToken() {
  cachedToken = null;
  inFlight = null;
}

function useOauth() {
  const [accessToken, setAccessToken] = useState(
    isValid(cachedToken) ? cachedToken.accessToken : ""
  );

  useEffect(() => {
    let mounted = true;

    // Reloads page if browser's back/forward (bfcache) restored it.
    const onPageShow = (event) => {
      const historyTraversal =
        event.persisted ||
        (typeof window.performance != "undefined" &&
          window.performance.navigation &&
          window.performance.navigation.type === 2);
      if (historyTraversal) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);

    getToken()
      .then((token) => {
        if (mounted) setAccessToken(token);
      })
      .catch(() => {});

    return () => {
      mounted = false;
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  return [accessToken];
}

export default useOauth;
